import React, { useRef, useState } from "react";
import styles from "./createModal.module.scss";
import MediumModal from "../../../../components/modals/medium/MediumModal";
import { FileIcon, TextField } from "@gataca/design-system/web";
import { gaColors } from "../../../../assets/globalConstants";
import cx from "classnames";
import { ICreationFormState } from "../BadgesList";
import { useTranslation } from "react-i18next";

type ICreateModalProps = {
  form: ICreationFormState;
  setFormState: (x?: any) => void;
  hideModal: () => void;
  uploadFile: (file: File, campaignName?: string) => void;
};

const CreateModal: React.FC<ICreateModalProps> = (props) => {
  const { form, setFormState, hideModal, uploadFile } = props;

  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement>();

  // Consts and states
  const [checkAliasErrors, setCheckAliasErrors] = useState(false);
  const [checkFileErrors, setCheckFileErrors] = useState(false);
  const [fileFieldHovered, setFileFieldHovered] = useState(false);

  const noAlias = !form?.campaignName?.trim()?.length;
  const noFile = !form?.file;
  const validForm = !noAlias && !noFile;

  // Functions
  const uploadFileFunc = () => {
    setCheckAliasErrors(true);
    setCheckFileErrors(true);
    validForm && uploadFile(form?.file!, form?.campaignName);
  };

  const handleFileInputClick = () => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = (event) => {
    setCheckFileErrors(false);
    const file = event.target?.files?.length
      ? event.target?.files[0]
      : undefined;
    if (file) {
      setFormState({ ...form, file: file });

      if (fileInputRef?.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <MediumModal
      title={"badgesList.creationModal.title"}
      description={"badgesList.creationModal.description"}
      closableByBg={false}
      onClose={hideModal}
      primaryButton={{
        onClick: uploadFileFunc,
        showText: true,
        state: "enable",
        color: "purple",
        textSize: "medium",
        text: "badgesList.creationModal.primaryButton",
      }}
      secondaryButton={{
        onClick: hideModal,
        showText: true,
        state: "enable",
        textSize: "medium",
        text: "public.cancel",
      }}>
      <div className={styles.formContainer}>
        <TextField
          helperText={
            noAlias && checkAliasErrors
              ? t("badgesList.creationModal.fieldHelperText")
              : undefined
          }
          label={{
            text: `${t("badgesList.creationModal.nameLabel")} *`,
          }}
          onChange={(e) => {
            setFormState({ ...form, campaignName: e.target.value });
            setCheckAliasErrors(false);
          }}
          placeholder={t("badgesList.creationModal.nameLabel")}
          state={noAlias && checkAliasErrors ? "error" : "enable"}
          textFieldSize="large"
          value={form.campaignName}
        />
        <div className={styles.fileFieldContainer}>
          <TextField
            textFieldSize={"large"}
            label={{
              text: `${t("badgesList.creationModal.fileLabel")} *`,
            }}
            state={
              noFile && checkFileErrors
                ? "error"
                : fileFieldHovered
                ? "hover"
                : "enable"
            }
            extraDescription={t("badgesList.creationModal.fileDesc")}
            leadingIcon={{
              Icon: FileIcon,
              style: "outlined",
              color: gaColors?.primaryColor,
            }}
            helperText={
              noFile && checkFileErrors
                ? t("badgesList.creationModal.fieldHelperText")
                : undefined
            }
            value={form.file ? form.file?.name : ""}
            placeholder={t("badgesList.creationModal.fileLabel")}
            onChange={(e) => {}}
          />

          <div
            className={cx(
              styles.hiddenContainer,
              noFile && checkFileErrors ? styles.hiddenContainerWithError : ""
            )}
            onMouseOver={() => setFileFieldHovered(true)}
            onMouseLeave={() => setFileFieldHovered(false)}
            onClick={handleFileInputClick}></div>

          <input
            className={styles.hiddenInput}
            type="file"
            ref={fileInputRef}
            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              handleFileChange(e);
              setCheckFileErrors(false);
            }}
          />
        </div>
      </div>
    </MediumModal>
  );
};

export default CreateModal;
